import { TZDate } from '@date-fns/tz';
import { format, formatDuration, intervalToDuration } from 'date-fns';
import { enGB, pl } from 'date-fns/locale';
import type { IsoDate, IsoDateTime, YearMonth } from '@overseer/contracts';
import { toIntlLocale } from './format';

/**
 * Every date operation in this application.
 *
 * Nothing else may construct a `Date`, format one, or decide what "today" is.
 * Timezone handling is the central risk in this application, and a whole
 * class of mistakes — a fixture date asserted against the wrong side of a DST
 * boundary, a `new Date(undefined)` reaching a rate lookup — becomes
 * unrepresentable when one module knows about zones and has its own tests.
 *
 * Two rules:
 *
 * 1. **The zone is Warsaw, not the browser's.** A workstation set to UTC, or a
 *    laptop taken abroad, must still see the working day the payroll is
 *    calculated against. `TZDate` from `@date-fns/tz` makes that explicit
 *    instead of relying on the host.
 * 2. **Calendar dates and instants are different types.** `IsoDate` is a Warsaw
 *    calendar day; `IsoDateTime` is a moment. The API is careful about the
 *    distinction and so is this.
 */

/** The one timezone this system runs in. Not configurable — see `CLAUDE.md`. */
export const TIMEZONE = 'Europe/Warsaw';

export type Locale = 'pl' | 'en';

const DATE_FNS_LOCALES = { pl, en: enGB } as const;

/** Now, as an instant. The only place the wall clock is read. */
export const now = (): Date => new Date();

/** The current Warsaw calendar date. */
export const today = (at: Date = now()): IsoDate =>
  format(new TZDate(at, TIMEZONE), 'yyyy-MM-dd') as IsoDate;

/** The Warsaw calendar date an instant falls on. */
export const toDate = (instant: IsoDateTime | Date): IsoDate =>
  format(new TZDate(new Date(instant), TIMEZONE), 'yyyy-MM-dd') as IsoDate;

/**
 * Whether a shift ends on a later Warsaw date than it started.
 *
 * Compared as **calendar dates in Warsaw**, not as a duration and not in UTC. A
 * shift is an overnight one because it crosses local midnight, so 22:00→02:00 is
 * four hours and counts, while 06:00→20:00 is fourteen and does not. Both
 * instants are converted with `toDate`, which is the same conversion the API used
 * to derive the day's `date` — so the icon cannot disagree with the column the
 * day is filed under.
 *
 * Null-safe on `endTime` because an unclosed day has none: nothing is known about
 * where it ends, so it is not claimed to be overnight.
 */
export const crossesMidnight = (
  startTime: IsoDateTime | Date | null | undefined,
  endTime: IsoDateTime | Date | null | undefined,
): boolean => (!startTime || !endTime ? false : toDate(startTime) !== toDate(endTime));

/** The current Warsaw year and month, 1-based to match the contract. */
export const currentYearMonth = (at: Date = now()): YearMonth => {
  const warsaw = new TZDate(at, TIMEZONE);

  return { year: warsaw.getFullYear(), month: warsaw.getMonth() + 1 };
};

/**
 * Wall-clock time of an instant, in Warsaw. `HH:mm`.
 *
 * An instant is not a time until you say where. `07:00Z` is `09:00` in Warsaw
 * in summer and `08:00` in winter, and both are correct.
 */
export const formatTime = (instant: IsoDateTime | Date): string =>
  format(new TZDate(new Date(instant), TIMEZONE), 'HH:mm');

/** A calendar date for display, e.g. `10.08.2026`. */
export const formatDate = (date: IsoDate): string => format(parseCalendarDate(date), 'dd.MM.yyyy');

/**
 * An instant as a calendar date and wall-clock time, in Warsaw, in the UI
 * locale — `10.08.2026, 14:30`.
 *
 * The one function every "when did this happen" column should call. Built on
 * `Intl.DateTimeFormat` rather than `date-fns/format` because it needs a
 * locale-shaped result (`en-GB` reads `10/08/2026, 14:30`, `pl-PL` reads
 * `10.08.2026, 14:30`), not just a translated month name.
 */
export const formatDateTime = (instant: IsoDateTime | Date, locale: Locale): string =>
  new Intl.DateTimeFormat(toIntlLocale(locale), {
    timeZone: TIMEZONE,
    dateStyle: 'short',
    timeStyle: 'short',
  }).format(new Date(instant));

/**
 * An instant with its seconds, in Warsaw, in the UI locale —
 * `10.08.2026, 14:30:05`. For a log line, where lines a second apart must
 * read apart.
 */
export const formatDateTimeSeconds = (instant: IsoDateTime | Date, locale: Locale): string =>
  new Intl.DateTimeFormat(toIntlLocale(locale), {
    timeZone: TIMEZONE,
    dateStyle: 'short',
    timeStyle: 'medium',
  }).format(new Date(instant));

/**
 * An instant as a `datetime-local` input's value, read in Warsaw —
 * `2026-08-10T14:30`. The browser's zone plays no part.
 */
export const toWarsawDateTimeInput = (instant: IsoDateTime | Date): string =>
  format(new TZDate(new Date(instant), TIMEZONE), "yyyy-MM-dd'T'HH:mm");

/**
 * A `datetime-local` input's value, meant as Warsaw wall-clock time, as an
 * instant. Undefined when it does not parse.
 */
export const instantFromWarsawDateTime = (value: string): IsoDateTime | undefined => {
  const match = /^(\d{4}-\d{2}-\d{2})T(\d{2}:\d{2})(?::\d{2}(?:\.\d+)?)?$/.exec(value.trim());

  return match ? instantFromWarsawTime(match[1] as IsoDate, match[2]) : undefined;
};

/**
 * The last millisecond of the minute an instant falls in: "up to 14:02"
 * takes in 14:02:59. Warsaw's offsets are whole hours, so the minute is the
 * same in UTC.
 */
export const endOfMinute = (instant: IsoDateTime | Date): IsoDateTime => {
  const minute = 60_000;

  return new Date(Math.floor(new Date(instant).getTime() / minute) * minute + minute - 1).toISOString() as IsoDateTime;
};

/**
 * An instant's Warsaw calendar date only, in the UI locale — `10.08.2026`.
 *
 * For a history entry that only needs the day, not the time it happened.
 */
export const formatInstantDate = (instant: IsoDateTime | Date, locale: Locale): string =>
  new Intl.DateTimeFormat(toIntlLocale(locale), {
    timeZone: TIMEZONE,
    dateStyle: 'short',
  }).format(new Date(instant));

/** Short weekday for a calendar date, localised — `pon`, `Mon`. */
export const formatWeekday = (date: IsoDate, locale: Locale): string =>
  format(parseCalendarDate(date), 'EEEEEE', { locale: DATE_FNS_LOCALES[locale] });

/** Full weekday for a calendar date, localised — `sobota`, `Saturday`. */
export const formatWeekdayLong = (date: IsoDate, locale: Locale): string =>
  format(parseCalendarDate(date), 'EEEE', { locale: DATE_FNS_LOCALES[locale] });

/** A month's short name for a chart axis, localised — `sie`, `Aug`. */
export const formatMonthShort = (month: number, locale: Locale): string =>
  format(new Date(2000, month - 1, 1), 'LLL', { locale: DATE_FNS_LOCALES[locale] });

/** Month and year for a header, localised — `sierpień 2026`, `August 2026`. */
export const formatMonth = ({ year, month }: YearMonth, locale: Locale): string =>
  format(new Date(year, month - 1, 1), 'LLLL yyyy', { locale: DATE_FNS_LOCALES[locale] });

/**
 * A duration in seconds as `H:MM`. **Every duration this app shows.**
 *
 * Hours are not capped at 24 and not zero-padded: a work day of 8.5 hours reads
 * `8:30`, and an open shift running 32 hours reads `32:00` rather than `08:00`,
 * which is what a naive `HH:mm` format of a duration would produce. That
 * distinction is the whole point on the dashboard.
 *
 * It replaced a decimal formatter on the timesheet, where `14.33` was a figure
 * nobody could read as 14 hours and 19 minutes without doing the arithmetic —
 * and the two formats side by side on one screen made `8.5` and `8:30` look like
 * different numbers. Charts scale their axes in decimal hours; nothing a person
 * reads as a length of time does.
 */
export const formatHoursMinutes = (seconds: number): string => {
  const safe = Math.max(0, Math.floor(seconds));
  const hours = Math.floor(safe / 3600);
  const minutes = Math.floor((safe % 3600) / 60);

  return `${hours}:${String(minutes).padStart(2, '0')}`;
};

/**
 * A duration in words, localised — "2 days 3 hours".
 *
 * For the stale-open list, where "1229:47" is technically correct and tells a
 * reader nothing.
 */
export const formatDurationWords = (seconds: number, locale: Locale): string => {
  const duration = intervalToDuration({ start: 0, end: Math.max(0, seconds) * 1000 });

  return formatDuration(duration, {
    format: ['years', 'months', 'days', 'hours'],
    zero: false,
    delimiter: ' ',
    locale: DATE_FNS_LOCALES[locale],
  });
};

/**
 * Seconds elapsed between a start instant and a server-supplied reference.
 *
 * `asOf` comes from the API precisely so the browser clock is not the baseline —
 * the old dashboard fed `countdown()` the local clock, so a workstation with a
 * skewed clock displayed a wrong elapsed time and nothing looked broken. Pass
 * `driftSeconds` to tick a display forward without re-reading the local clock as
 * a source of truth.
 */
export const elapsedSince = (
  startTime: IsoDateTime | Date,
  asOf: IsoDateTime | Date,
  driftSeconds = 0,
): number => {
  const elapsed = (new Date(asOf).getTime() - new Date(startTime).getTime()) / 1000;

  return Math.max(0, Math.floor(elapsed) + Math.floor(driftSeconds));
};

/**
 * A Warsaw wall-clock time on a given calendar date, as an instant.
 *
 * The conversion the correction form needs, and the one most likely to be got
 * wrong: someone types `06:30` against a date, and that means 06:30 *in Warsaw*,
 * which is 04:30Z in summer and 05:30Z in winter. Building it with
 * `new Date(\`\${date}T\${time}\`)` would interpret it in the browser's zone, so a
 * workstation set to UTC would silently store every correction two hours out.
 *
 * Returns undefined for an unparseable time rather than an Invalid Date: an
 * Invalid Date that survives into a request body, or reaches a rate lookup,
 * is the harder bug to trace.
 */
export const instantFromWarsawTime = (
  date: IsoDate,
  time: string,
): IsoDateTime | undefined => {
  const match = /^(\d{1,2}):(\d{2})$/.exec(time.trim());

  if (!match) {
    return undefined;
  }

  const [hours, minutes] = [Number(match[1]), Number(match[2])];

  if (hours > 23 || minutes > 59) {
    return undefined;
  }

  const [year, month, day] = date.split('-').map(Number);
  const warsaw = new TZDate(year, month - 1, day, hours, minutes, 0, TIMEZONE);

  // Through `new Date(...).toISOString()` rather than `warsaw.toISOString()`:
  // TZDate's own serialisation keeps the zone, giving `2026-07-15T06:30:00+02:00`.
  // That is the same instant and the contract accepts an offset, but everything
  // else on the wire is UTC `Z`, and one canonical form on the boundary is worth
  // more than saving a conversion.
  return new Date(warsaw.getTime()).toISOString() as IsoDateTime;
};

/**
 * The calendar date `days` after `date`.
 *
 * Needed because a work day can end on the following date: a shift running
 * 22:00 -> 06:00 is one work day, stored against the date it *started*. The
 * correction form therefore has to be able to place an exit on the next day.
 *
 * Arithmetic on the date parts, not on an instant plus 86 400 000 ms — the
 * latter is wrong twice a year, when a Warsaw day is 23 or 25 hours long.
 */
export const addDays = (date: IsoDate, days: number): IsoDate => {
  const [year, month, day] = date.split('-').map(Number);

  return format(new Date(year, month - 1, day + days), 'yyyy-MM-dd') as IsoDate;
};

/**
 * The same calendar date, shifted by whole years.
 *
 * 29 February clamps to 28 February in a non-leap year — JavaScript's
 * `setFullYear` would roll it to 1 March, and that is a different day, not a
 * missing one.
 */
export const addYears = (date: IsoDate, years: number): IsoDate => {
  const [year, month, day] = date.split('-').map(Number);
  const targetYear = year + years;
  const lastDayOfMonth = new Date(targetYear, month, 0).getDate();

  return format(
    new Date(targetYear, month - 1, Math.min(day, lastDayOfMonth)),
    'yyyy-MM-dd',
  ) as IsoDate;
};

/**
 * The nearest date one year earlier that falls on the same weekday.
 *
 * A Saturday is compared to a Saturday, not to the same calendar date if that
 * date was a Friday. For 1 August 2026 (Saturday) that is 2 August 2025
 * (Saturday), not 1 August 2025 (Friday).
 *
 * "Nearest" is unambiguous for a one-year shift: the weekday moves by one day,
 * or by two when a leap day sits between the two dates. `getDay` wrapping
 * (Sunday vs Saturday looking like a jump of six) is folded back into that
 * window so the candidate stays next to the calendar-equivalent date.
 */
export const similarWeekdayLastYear = (date: IsoDate): IsoDate => {
  const lastYear = addYears(date, -1);
  const want = parseCalendarDate(date).getDay();
  const have = parseCalendarDate(lastYear).getDay();
  let delta = want - have;

  if (delta > 3) delta -= 7;
  if (delta < -3) delta += 7;

  return addDays(lastYear, delta);
};

/**
 * The instant a shift ends, given the day it started and a wall-clock time.
 *
 * An exit earlier in the clock than the entry means the shift crossed midnight,
 * so the exit belongs to the following date. This is what the data already looks
 * like — the local database holds shifts entered 14:24 and exited 05:26 — and
 * building the exit naively on the start date puts it nine hours *before* the
 * entry, which the API rejects.
 *
 * Returns the instant and the date it landed on, so the form can say "+1 day"
 * rather than rolling silently.
 */
export const endInstantFor = (
  startDate: IsoDate,
  startTime: string,
  endTime: string,
): { instant: IsoDateTime; date: IsoDate } | undefined => {
  const start = instantFromWarsawTime(startDate, startTime);
  const sameDay = instantFromWarsawTime(startDate, endTime);

  if (!sameDay) {
    return undefined;
  }

  if (!start || new Date(sameDay).getTime() > new Date(start).getTime()) {
    return { instant: sameDay, date: startDate };
  }

  const nextDate = addDays(startDate, 1);
  const nextDay = instantFromWarsawTime(nextDate, endTime);

  return nextDay ? { instant: nextDay, date: nextDate } : undefined;
};

/** Adds or subtracts whole months, staying 1-based and rolling the year. */
export const shiftMonth = ({ year, month }: YearMonth, delta: number): YearMonth => {
  const zeroBased = year * 12 + (month - 1) + delta;

  return { year: Math.floor(zeroBased / 12), month: (zeroBased % 12) + 1 };
};

/**
 * Parses `YYYY-MM-DD` into a Date at local midnight.
 *
 * Deliberately not `new Date('2026-08-10')`, which JavaScript parses as **UTC**
 * midnight — so in any negative-offset zone it is the previous day, and
 * formatting it back yields a different date than went in. Splitting the parts
 * and using the numeric constructor keeps a calendar date a calendar date.
 */
const parseCalendarDate = (date: IsoDate): Date => {
  const [year, month, day] = date.split('-').map(Number);

  return new Date(year, month - 1, day);
};
